import { Box, Typography, useTheme, alpha } from '@mui/material'
import React from 'react'
import RichTextDescription from './RichTextDescription'

const GenerateTable = ({ options, section = '' }) => {
  const theme = useTheme()
  const headers = options.length > 0 ? Object.keys(options[0]) : []

  // Build grid template from xs values or equal distribution
  const gridTemplate = headers
    .map(h => {
      const sample = options[0]?.[h]
      const fr = sample?.xs ?? (h === 'Service' ? 2.5 : 1)
      return `${fr}fr`
    })
    .join(' ')

  const headerCellSx = {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: 'text.secondary',
    py: 1,
    px: 1.5,
  }

  const cellSx = {
    fontSize: 13,
    fontWeight: 500,
    color: 'text.primary',
    py: 0.75,
    px: 1.5,
    fontVariantNumeric: 'tabular-nums' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }

  return (
    <Box sx={{ width: '100%' }}>
      {section && (
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{section}</Typography>
        </Box>
      )}

      {/* Column Headers */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.text.primary, 0.02),
        }}
      >
        {headers.map((header, i) => (
          <Typography
            key={i}
            sx={{
              ...headerCellSx,
              textAlign: header === 'Service' ? 'left' : 'center',
            }}
          >
            {header}
          </Typography>
        ))}
      </Box>

      {/* Data Rows */}
      {options.map((entry, index) => (
        <Box
          key={index}
          sx={{
            display: 'grid',
            gridTemplateColumns: gridTemplate,
            alignItems: 'center',
            borderBottom: index < options.length - 1 ? '1px solid' : 'none',
            borderColor: 'divider',
            transition: 'background-color 0.15s',
            '&:hover': {
              bgcolor: alpha(theme.palette.text.primary, 0.03),
            },
          }}
        >
          {headers.map((header, headerIndex) => {
            const cell = entry[header]
            const isService = header === 'Service'
            return (
              <Box
                key={headerIndex}
                sx={{
                  ...cellSx,
                  textAlign: isService ? 'left' : 'center',
                  color: cell?.color || theme.palette.text.primary,
                  py: isService && cell?.description ? 0.5 : 0.75,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'inherit',
                    display: 'flex',
                    alignItems: isService ? 'center' : 'center',
                    justifyContent: isService ? 'flex-start' : 'center',
                    gap: 0.5,
                  }}
                >
                  {cell?.type === 'number' ? `$${cell.value}` : cell?.value}
                </Typography>
                {isService && cell?.description && (
                  <RichTextDescription description={cell.description} />
                )}
              </Box>
            )
          })}
        </Box>
      ))}
    </Box>
  )
}

export default GenerateTable
